import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api.ts';
import type { ConflictPolicy } from './queries.ts';

/**
 * Documents, from the client's side.
 *
 * Mirrors the sheet queries deliberately: the same keys, the same
 * invalidation, the same shape of hook. Two document types that behave
 * differently for no reason would be two things to learn.
 */

export interface PageMargins {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

export interface PageBorder {
  style: 'solid' | 'dashed' | 'dotted' | 'double';
  color: string;
  width: number;
}

export interface DocumentLayout {
  paper?: 'A4' | 'Letter';
  orientation?: 'portrait' | 'landscape';
  margins?: PageMargins;

  header?: string;
  footer?: string;
  differentFirstPage?: boolean;
  firstPageHeader?: string;
  firstPageFooter?: string;
  /** Millimetres from the paper's edge. */
  headerDistance?: number;
  footerDistance?: number;

  pageNumbers?: boolean;
  pageNumberPosition?: 'footerLeft' | 'footerCenter' | 'footerRight';

  columns?: number;
  columnGap?: number;

  /** Measured in the editor; the PDF needs it because nothing can measure
   *  a header on the server. */
  headerHeight?: number;
  footerHeight?: number;
  /**
   * How far the footer reaches above its own top edge, in millimetres.
   *
   * Nearly always nothing. It is not nothing for an imported letterhead,
   * whose footer is a picture anchored above the line the box sits on, and
   * that overhang is part of the strip the body has to keep clear.
   */
  footerRise?: number;

  pageColor?: string;
  border?: PageBorder;

  /**
   * The watermark, with the properties Word's own dialog offers.
   *
   * Flat fields rather than a nested object, so a document saved before any
   * of them existed still opens: every one of them has a default.
   */
  watermark?: string;
  watermarkFont?: string;
  /** Points. Zero means "auto", which scales the text to the page. */
  watermarkSize?: number;
  watermarkColor?: string;
  watermarkBold?: boolean;
  watermarkItalic?: boolean;
  /** Word's Semitransparent checkbox, as an actual opacity. */
  watermarkOpacity?: number;
  watermarkLayout?: 'diagonal' | 'horizontal';
  /** A picture watermark, which Word offers instead of text. */
  watermarkImage?: string;
  /** One per page, the last repeating, from a document designed page by page. */
  /**
   * Where the page changes shape partway down — Word's section breaks.
   *
   * The document's own paper, orientation and margins describe the section
   * that governs most of the text. These say where the others begin, by block
   * index, and what each of them changes.
   */
  sections?: {
    fromBlock: number;
    paper?: 'A4' | 'Letter';
    orientation?: 'portrait' | 'landscape';
    margins?: { top: number; right: number; bottom: number; left: number };
  }[];
  pageBackgrounds?: string[];
  /** The block each of those was anchored to, in the same order. */
  pageBackgroundAnchors?: number[];
  /** Furniture drawn on every page — a frame, a border, a texture. */
  pageFrames?: string[];
  /** The same for the title page, where the document dresses it differently. */
  firstPageFrames?: string[];
  /** Percent of the page width. */
  watermarkImageScale?: number;
  /** Word's Washout: faded so text stays readable over it. */
  watermarkWashout?: boolean;
}

export const WATERMARK_FONTS = [
  'Calibri',
  'Cambria',
  'Georgia',
  'Times New Roman',
  'Arial',
  'Verdana',
  'Impact',
];

/** Word's defaults, so an untouched watermark looks like Word's. */
export const WATERMARK_DEFAULTS = {
  font: 'Calibri',
  size: 0,
  color: '#c0c0c0',
  opacity: 0.5,
  layout: 'diagonal' as const,
  imageScale: 100,
  washout: true,
};

/** Word's own default distance from the paper's edge, in millimetres. */
export const DEFAULT_HEADER_DISTANCE = 12.7;

export interface DocumentSummary {
  id: string;
  name: string;
  folderId: string | null;
  isStarred: boolean;
  revision: number;
  words: number;
  createdAt: string;
  updatedAt: string;
}

export interface DocumentRecord extends DocumentSummary {
  content: string;
  layout: DocumentLayout;
}

export interface DocumentTemplate {
  id: string;
  name: string;
  description: string;
}

/** A4 with Word's one-inch margins, in millimetres. */
export const DEFAULT_MARGINS: PageMargins = { top: 25.4, right: 25.4, bottom: 25.4, left: 25.4 };

export const PAPER_SIZES: Record<'A4' | 'Letter', { width: number; height: number }> = {
  A4: { width: 210, height: 297 },
  Letter: { width: 215.9, height: 279.4 },
};

export const documentKeys = {
  all: ['documents'] as const,
  inFolder: (folderId: string | null) => ['documents', 'folder', folderId] as const,
  one: (id: string) => ['documents', id] as const,
  templates: ['documents', 'templates'] as const,
};

export function useDocuments(folderId: string | null) {
  return useQuery({
    queryKey: documentKeys.inFolder(folderId),
    queryFn: () =>
      api.get<DocumentSummary[]>(`/api/documents${folderId ? `?folderId=${folderId}` : ''}`),
  });
}

export function useDocument(id: string) {
  return useQuery({
    queryKey: documentKeys.one(id),
    queryFn: () => api.get<DocumentRecord>(`/api/documents/${id}`),
    enabled: id !== '',
    // The editor owns the document once it is open; refetching underneath it
    // would replace what the user is typing into.
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
}

export function useDocumentTemplates() {
  return useQuery({
    queryKey: documentKeys.templates,
    queryFn: () => api.get<DocumentTemplate[]>('/api/documents/templates'),
    staleTime: 60 * 60 * 1000,
  });
}

export function useCreateDocument() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      name: string;
      folderId: string | null;
      template?: string;
      /** What to do about a name the folder already holds. */
      onConflict?: ConflictPolicy;
    }) => api.post<DocumentRecord>('/api/documents', input),
    onSuccess: () => void client.invalidateQueries({ queryKey: documentKeys.all }),
  });
}

export function useRenameDocument() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      api.post<DocumentRecord>(`/api/documents/${id}/rename`, { name }),
    onSuccess: () => void client.invalidateQueries({ queryKey: documentKeys.all }),
  });
}

export function useMoveDocument() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, folderId }: { id: string; folderId: string | null }) =>
      api.post<DocumentRecord>(`/api/documents/${id}/move`, { folderId }),
    onSuccess: () => void client.invalidateQueries({ queryKey: documentKeys.all }),
  });
}

export function useStarDocument() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, isStarred }: { id: string; isStarred: boolean }) =>
      api.post<DocumentRecord>(`/api/documents/${id}/star`, { isStarred }),
    onSuccess: () => void client.invalidateQueries({ queryKey: documentKeys.all }),
  });
}

export function useTrashDocument() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/api/documents/${id}`),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: documentKeys.all });
      void client.invalidateQueries({ queryKey: ['trash'] });
    },
  });
}

/** Turn an uploaded Word file into an editable document. */
export function useDocumentFromFile() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { fileId: string; name?: string; folderId?: string | null }) =>
      api.post<DocumentRecord>('/api/documents/from-file', input),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: documentKeys.all });
      void client.invalidateQueries({ queryKey: ['files'] });
      void client.invalidateQueries({ queryKey: ['folders'] });
    },
  });
}

/**
 * Word files that open in the editor rather than the viewer.
 *
 * Narrower than the server's import list on purpose, because this decides
 * what a click does. `.doc` is excluded: the importer refuses the old binary
 * format with an explanation, so sending a click there would trade a document
 * the viewer can render for an error message. `.txt` and `.html` are excluded
 * because the viewer shows them as themselves, immediately, with no
 * conversion — turning one into a rich document on a single click is a
 * heavier answer than the question.
 *
 * What is left is the case the client actually has: a .docx, which the viewer
 * could only ever show as a PDF rendering of itself. A picture of a letter is
 * not a letter you can finish writing.
 */
export const EDITABLE_DOCUMENT_EXTENSIONS = ['.docx', '.rtf', '.odt'];

export function isEditableDocument(filename: string): boolean {
  const lower = filename.toLowerCase();
  return EDITABLE_DOCUMENT_EXTENSIONS.some((extension) => lower.endsWith(extension));
}
